import type { AxiosInstance } from 'axios'
import type { Pinia } from 'pinia'
import { getAccessToken } from '@/helpers/auth.ts'
import {
    BAD_REQUEST_CODE,
    FORBIDDEN_CODE,
    UNAUTHORIZED_CODE,
    UNPROCESSABLE_CONTENT_CODE,
} from '@/config/httpCodes.ts'
import { useAccountStore } from '@/stores/account.ts'
import router from '@/router'
import { routeNames } from '@/router/names.ts'
import { isAxiosError } from 'axios'
import {
    CHILD_PROTECT_DOCUMENT,
    CURRENT_TERMS_DOCUMENT,
} from '@/config/requiredDocumentsCodes.ts'
import { DocumentToAcceptError } from '@/models/DocumentToAcceptError.ts'
import { useErrorStore } from '@/stores/error.ts'
import { showToast } from '@/services/modules/toastService.ts'
import i18n from '@/i18n'
import {
    findAssignedSubAccount,
    findFirstSubAccountRequiringTermsAcceptance,
    getAssignedSubAccountIds,
    requiresCurrentTermsAcceptance,
} from '@/helpers/guardianTerms.ts'

const CURRENT_TERMS_NOT_ACCEPTED_CODE = 'CURRENT_TERMS_NOT_ACCEPTED'

export default (axios: AxiosInstance, pinia: Pinia) => {
    axios.interceptors.request.use((config) => {
        const token = getAccessToken()

        if (token && !config.omitAuthorization) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        return config
    })

    axios.interceptors.response.use(
        (response) => {
            return response
        },
        async (error) => {
            if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_CONTENT_CODE) {
                const errorStore = useErrorStore(pinia)

                errorStore.setGlobalError(error.response?.data?.message || '')

                return Promise.reject(error)
            }

            if (isAxiosError(error) && error.response?.data.error === CHILD_PROTECT_DOCUMENT) {
                await router.push({
                    name: routeNames.CHILD_PROTECTION_VIEW_NAME,
                })

                return Promise.reject(
                    new DocumentToAcceptError(
                        CHILD_PROTECT_DOCUMENT,
                        error.response.data.message || '',
                    ),
                )
            }

            if (
                isAxiosError(error) &&
                error.response?.status === FORBIDDEN_CODE &&
                (error.response.data?.code === CURRENT_TERMS_NOT_ACCEPTED_CODE ||
                    error.response.data?.error === CURRENT_TERMS_DOCUMENT)
            ) {
                const accountStore = useAccountStore(pinia)
                const routeSubAccountId = router.currentRoute.value.params.subAccountId
                    ? String(router.currentRoute.value.params.subAccountId)
                    : null
                const routeAssignedSubAccount = findAssignedSubAccount(
                    accountStore.accountData,
                    routeSubAccountId,
                )
                const firstSubAccountRequiringTerms =
                    findFirstSubAccountRequiringTermsAcceptance(accountStore.accountData)
                const fallbackSubAccountId = getAssignedSubAccountIds(accountStore.accountData)[0]
                const subAccountId =
                    (requiresCurrentTermsAcceptance(routeAssignedSubAccount)
                        ? routeAssignedSubAccount?.subaccountId
                        : null) ||
                    firstSubAccountRequiringTerms?.subaccountId ||
                    fallbackSubAccountId ||
                    null

                if (
                    router.currentRoute.value.name !== routeNames.GUARDIAN_TERMS_VIEW_NAME
                ) {
                    await router.push({
                        name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
                        params: subAccountId ? { subAccountId } : undefined,
                    })
                }

                return Promise.reject(
                    new DocumentToAcceptError(
                        CURRENT_TERMS_DOCUMENT,
                        error.response.data?.message || i18n.global.t('Document approval required'),
                        { subAccountId },
                    ),
                )
            }

            if (
                isAxiosError(error) &&
                error.status === UNAUTHORIZED_CODE &&
                error.config?.url === 'guardian/login_check'
            ) {
                // endpoint 'guardian/login_check' returns 401 on bad credentials and we don't want handle it like unauthorized response
                error.status = BAD_REQUEST_CODE

                return Promise.reject(error)
            }

            if (isAxiosError(error) && error.response?.status === UNAUTHORIZED_CODE) {
                const accountStore = useAccountStore(pinia)

                await accountStore.onUnauthorizedResponse()

                return Promise.reject(error)
            }

            if (isAxiosError(error) && error.code === 'ECONNABORTED') {
                showToast({
                    summary: i18n.global.t('Timeout error'),
                    severity: 'error',
                    life: 4000,
                })
            }

            return Promise.reject(error)
        },
    )
}
